import { useEffect, useState, useCallback } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { Trash2, PlusCircle } from 'lucide-react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AssetFormProps = {
  assetId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type OptionItem = { id: string | number; name: string }

type Component = {
  component_type: string
  component_model: string
  component_serial: string
}

type AssetState = {
  brand_id: string
  category_id: string
  tag_number: string
  model: string
  serial_number: string
  function: string
  sw_provider: string
  warranty_date: string
  order_received_date: string
  ram: string
  disk_space: string
  ms_office: string
  cpu: string
  os_version: string
  os_status: string
  processor: string
  ip_address: string
  mac_address: string
  active: boolean
}

const emptyAsset: AssetState = {
  brand_id: '',
  category_id: '',
  tag_number: '',
  model: '',
  serial_number: '',
  function: '',
  sw_provider: '',
  warranty_date: '',
  order_received_date: '',
  ram: '',
  disk_space: '',
  ms_office: '',
  cpu: '',
  os_version: '',
  os_status: '',
  processor: '',
  ip_address: '',
  mac_address: '',
  active: true,
}

const emptyComponent: Component = { component_type: '', component_model: '', component_serial: '' }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalise a response envelope to a plain array. */
function toArray<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[]
  const d = (res as { data?: unknown })?.data
  if (Array.isArray(d)) return d as T[]
  const dd = (d as { data?: unknown })?.data
  if (Array.isArray(dd)) return dd as T[]
  return []
}

/** Return the list with the current saved value prepended if it's missing. */
function withCurrent(options: OptionItem[], currentName: string): OptionItem[] {
  const list = [...options]
  const current = (currentName || '').trim()
  if (current && !list.some((o) => o.name === current)) {
    list.unshift({ id: `__current__${current}`, name: current })
  }
  return list.sort((a, b) => a.name.localeCompare(b.name))
}

/** Filter specificCategories by parent_slug. */
function filterByParentSlug(items: OptionItem[], parentSlug: string): OptionItem[] {
  return items
    .filter((item) => {
      const s = (item as unknown as { parent_slug?: string; parent?: { slug?: string } })
      const slug = s.parent_slug || s.parent?.slug
      return slug && slug === parentSlug
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Extract numeric prefix for sorting RAM / Disk values (e.g. "500GB" → 500). */
function numericPrefix(s: string): number {
  const m = s.match(/(\d+)/)
  return m ? parseInt(m[1], 10) : 0
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AssetForm({ assetId, onSuccess, onCancel }: AssetFormProps) {
  const { showError, showSuccess } = useToastNotification()

  // ── loading / saving state ──────────────────────────────────────────────
  const [loading, setLoading] = useState(!!assetId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [auditCount, setAuditCount] = useState(0)

  // ── form data ────────────────────────────────────────────────────────────
  const [asset, setAsset] = useState<AssetState>(emptyAsset)
  const [components, setComponents] = useState<Component[]>([])
  const [newComponent, setNewComponent] = useState<Component>(emptyComponent)

  // ── option lists ─────────────────────────────────────────────────────────
  const [brands, setBrands] = useState<OptionItem[]>([])
  const [categories, setCategories] = useState<OptionItem[]>([])
  const [brandModels, setBrandModels] = useState<OptionItem[]>([])
  const [functions, setFunctions] = useState<OptionItem[]>([])
  const [specificCategories, setSpecificCategories] = useState<OptionItem[]>([])
  const [cpuOptions, setCpuOptions] = useState<OptionItem[]>([])
  const [osVersionOptions, setOsVersionOptions] = useState<OptionItem[]>([])
  const [processorOptions, setProcessorOptions] = useState<OptionItem[]>([])

  const isLocked = auditCount > 0

  // ── static option helpers ─────────────────────────────────────────────────
  const swProviderOptions = useCallback(
    () => filterByParentSlug(specificCategories, 'sw-provider'),
    [specificCategories]
  )
  const ramOptions = useCallback(
    () =>
      filterByParentSlug(specificCategories, 'ram').sort(
        (a, b) => numericPrefix(a.name) - numericPrefix(b.name)
      ),
    [specificCategories]
  )
  const diskOptions = useCallback(
    () =>
      filterByParentSlug(specificCategories, 'disk-space').sort(
        (a, b) => numericPrefix(a.name) - numericPrefix(b.name)
      ),
    [specificCategories]
  )
  const msOfficeOptions = useCallback(
    () => filterByParentSlug(specificCategories, 'ms-office'),
    [specificCategories]
  )

  const functionOptions = useCallback(
    () => withCurrent(functions, asset.function),
    [functions, asset.function]
  )

  // ── initial data loads ────────────────────────────────────────────────────

  // Brands
  useEffect(() => {
    apiClient
      .get('/options/brands')
      .then((res) => setBrands(toArray<OptionItem>(res.data)))
      .catch(() => setBrands([]))
  }, [])

  // Specific-categories (spec dropdowns + SW provider)
  useEffect(() => {
    apiClient
      .get('/options/specific-categories')
      .then((res) => setSpecificCategories(toArray<OptionItem>(res.data)))
      .catch(() => setSpecificCategories([]))
  }, [])

  // CPU / OS version / Processor
  useEffect(() => {
    apiClient
      .get('/options/cpu')
      .then((res) => setCpuOptions(toArray<OptionItem>(res.data)))
      .catch(() => setCpuOptions([]))
    apiClient
      .get('/options/os-version')
      .then((res) => setOsVersionOptions(toArray<OptionItem>(res.data)))
      .catch(() => setOsVersionOptions([]))
    apiClient
      .get('/options/processor')
      .then((res) => setProcessorOptions(toArray<OptionItem>(res.data)))
      .catch(() => setProcessorOptions([]))
  }, [])

  // ── derived: categories from selected brand ────────────────────────────────
  useEffect(() => {
    if (!asset.brand_id) {
      setCategories([])
      return
    }
    const brand = brands.find((b) => String(b.id) === String(asset.brand_id))
    if (!brand) {
      setCategories([])
      return
    }
    try {
      const cats = JSON.parse(
        (brand as unknown as { categories?: string }).categories ?? '[]'
      )
      setCategories(Array.isArray(cats) ? cats : [])
    } catch {
      setCategories([])
    }
  }, [asset.brand_id, brands])

  // ── derived: brand models ─────────────────────────────────────────────────
  useEffect(() => {
    if (!asset.brand_id || !asset.category_id) {
      setBrandModels([])
      return
    }
    apiClient
      .get('/options/brand-models', {
        params: { brand_id: asset.brand_id, category_id: asset.category_id },
      })
      .then((res) => {
        const arr = toArray<OptionItem>(res.data)
        setBrandModels(arr)
      })
      .catch(() => setBrandModels([]))
  }, [asset.brand_id, asset.category_id])

  // ── derived: functions ────────────────────────────────────────────────────
  useEffect(() => {
    if (!asset.category_id) {
      setFunctions([])
      return
    }
    apiClient
      .get(`/options/functions/${asset.category_id}`)
      .then((res) => {
        const arr = toArray<OptionItem>(res.data).filter((i) => i?.name)
        setFunctions(arr)
      })
      .catch(() => setFunctions([]))
  }, [asset.category_id])

  // ── os_status reset when OS version is not Windows ────────────────────────
  useEffect(() => {
    if (!/windows/i.test(asset.os_version)) {
      setAsset((prev) => ({ ...prev, os_status: '' }))
    }
  }, [asset.os_version])

  // ── load existing asset ───────────────────────────────────────────────────
  const loadOne = useCallback(async () => {
    if (!assetId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/inventory-management/assets/${assetId}`)
      const r = data?.data ?? data
      setAuditCount(r.audit_count ?? 0)
      setComponents(r.components ?? [])
      setAsset({
        brand_id: r.brand_id != null ? String(r.brand_id) : '',
        category_id: r.category_id != null ? String(r.category_id) : '',
        tag_number: r.tag_number ?? '',
        model: r.model ?? '',
        serial_number: r.serial_number ?? '',
        function: r.function ?? '',
        sw_provider: r.sw_provider ?? '',
        warranty_date: formatDate(r.warranty_date),
        order_received_date: formatDate(r.order_received_date),
        ram: r.ram ?? (r.specifications?.ram ?? ''),
        disk_space: r.disk_space ?? (r.specifications?.disk_space ?? ''),
        ms_office: r.ms_office ?? (r.specifications?.ms_office ?? ''),
        cpu: r.cpu ?? (r.specifications?.cpu ?? ''),
        os_version: r.os_version ?? (r.specifications?.os_version ?? ''),
        os_status: r.os_status ?? (r.specifications?.os_status ?? ''),
        processor: r.processor ?? (r.specifications?.processor ?? ''),
        ip_address: r.ip_address ?? (r.specifications?.ip_address ?? ''),
        mac_address: r.mac_address ?? (r.specifications?.mac_address ?? ''),
        active: r.active !== false && r.active !== 'Inactive',
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [assetId, showError])

  useEffect(() => {
    if (assetId) {
      loadOne()
    } else {
      setAsset(emptyAsset)
      setComponents([])
      setAuditCount(0)
    }
  }, [assetId, loadOne])

  // ── field updater ────────────────────────────────────────────────────────
  const set = <K extends keyof AssetState>(field: K, value: AssetState[K]) => {
    setAsset((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const n = { ...prev }
        delete n[field]
        return n
      })
    }
  }

  // ── brand change: reset dependents ───────────────────────────────────────
  const handleBrandChange = (brandId: string) => {
    setAsset((prev) => ({ ...prev, brand_id: brandId, category_id: '', model: '', function: '' }))
  }

  // ── category change: reset dependents ────────────────────────────────────
  const handleCategoryChange = (categoryId: string) => {
    setAsset((prev) => ({ ...prev, category_id: categoryId, model: '', function: '' }))
  }

  // ── component handlers ────────────────────────────────────────────────────
  const handleAddComponent = () => {
    if (!newComponent.component_type || !newComponent.component_model || !newComponent.component_serial) {
      return
    }
    setComponents((prev) => [...prev, { ...newComponent }])
    setNewComponent(emptyComponent)
  }

  const handleDeleteComponent = (index: number) => {
    setComponents((prev) => prev.filter((_, i) => i !== index))
  }

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        ...asset,
        tag_number: asset.tag_number.toUpperCase().trim(),
        components,
      }
      if (assetId) {
        await apiClient.put(`/inventory-management/assets/${assetId}`, payload)
        showSuccess('Success', 'Asset updated successfully')
      } else {
        await apiClient.post('/inventory-management/assets', payload)
        showSuccess('Success', 'Asset created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  const isWindows = /windows/i.test(asset.os_version)
  const modelOptionsWithCurrent = withCurrent(brandModels, asset.model)

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">

        {/* Lock warning */}
        {isLocked && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Locked</AlertTitle>
              <AlertDescription>
                This asset has been audited ({auditCount} audit{auditCount !== 1 ? 's' : ''}) and can no longer be edited.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* ── Basic Info section ── */}
        <VStack spacing={4} align="stretch">

          {/* Brand */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Brand <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.brand_id?.length} isRequired>
              <Select
                placeholder="Select Brand"
                value={asset.brand_id}
                onChange={(e) => handleBrandChange(e.target.value)}
                isDisabled={isLocked}
              >
                {brands.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.brand_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Category / Machine Type */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Category / Machine Type <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.category_id?.length} isRequired>
              <Select
                placeholder="Select Category"
                value={asset.category_id}
                onChange={(e) => handleCategoryChange(e.target.value)}
                isDisabled={isLocked || !asset.brand_id}
              >
                {categories.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {(c as unknown as { name?: string; label?: string }).name ??
                      (c as unknown as { label?: string }).label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.category_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Asset Tag / POS TID */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Asset Tag / POS TID <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.tag_number?.length} isRequired>
              <Input
                value={asset.tag_number}
                onChange={(e) => set('tag_number', e.target.value.toUpperCase())}
                placeholder="e.g. POS-2024-001 or NA"
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.tag_number?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Model */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Model <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.model?.length} isRequired>
              <Select
                placeholder="Select Model"
                value={asset.model}
                onChange={(e) => set('model', e.target.value)}
                isDisabled={isLocked || !asset.brand_id || !asset.category_id}
              >
                {modelOptionsWithCurrent.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.model?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Serial Number */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Serial Number <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.serial_number?.length} isRequired>
              <Input
                value={asset.serial_number}
                onChange={(e) => set('serial_number', e.target.value)}
                placeholder="e.g. SN123456789"
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.serial_number?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Function */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Function <Box as="span" color="red.500">*</Box>
            </FormLabel>
            <FormControl isInvalid={!!errors.function?.length} isRequired>
              <Select
                placeholder="Select Function"
                value={asset.function}
                onChange={(e) => set('function', e.target.value)}
                isDisabled={isLocked}
              >
                {functionOptions().map((f) => (
                  <option key={String(f.id)} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.function?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* SW Provider */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">SW Provider</FormLabel>
            <FormControl isInvalid={!!errors.sw_provider?.length}>
              <Select
                placeholder="Select SW Provider"
                value={asset.sw_provider}
                onChange={(e) => set('sw_provider', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(swProviderOptions(), asset.sw_provider).map((p) => (
                  <option key={String(p.id)} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.sw_provider?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Warranty Expiration Date */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Warranty Expiration Date</FormLabel>
            <FormControl isInvalid={!!errors.warranty_date?.length}>
              <Input
                type="date"
                value={asset.warranty_date}
                onChange={(e) => set('warranty_date', e.target.value)}
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.warranty_date?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Order Received Date */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Order Received Date</FormLabel>
            <FormControl isInvalid={!!errors.order_received_date?.length}>
              <Input
                type="date"
                value={asset.order_received_date}
                onChange={(e) => set('order_received_date', e.target.value)}
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.order_received_date?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        </VStack>

        {/* ── Specifications section ── */}
        <Divider />
        <Heading size="sm" color="gray.600">
          Asset Specifications
        </Heading>

        <VStack spacing={4} align="stretch">

          {/* RAM */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">RAM</FormLabel>
            <FormControl isInvalid={!!errors.ram?.length}>
              <Select
                placeholder="Select RAM"
                value={asset.ram}
                onChange={(e) => set('ram', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(ramOptions(), asset.ram).map((r) => (
                  <option key={String(r.id)} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.ram?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Disk Space */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Disk Space</FormLabel>
            <FormControl isInvalid={!!errors.disk_space?.length}>
              <Select
                placeholder="Select Disk Space"
                value={asset.disk_space}
                onChange={(e) => set('disk_space', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(diskOptions(), asset.disk_space).map((d) => (
                  <option key={String(d.id)} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.disk_space?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* OS Version */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">OS Version</FormLabel>
            <FormControl isInvalid={!!errors.os_version?.length}>
              <Select
                placeholder="Select OS Version"
                value={asset.os_version}
                onChange={(e) => set('os_version', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(osVersionOptions, asset.os_version).map((o) => (
                  <option key={String(o.id)} value={o.name}>
                    {o.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.os_version?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* OS Status — only when OS version contains 'windows' */}
          {isWindows && (
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">Windows Activation Status</FormLabel>
              <FormControl isInvalid={!!errors.os_status?.length}>
                <Select
                  placeholder="Select Activation Status"
                  value={asset.os_status}
                  onChange={(e) => set('os_status', e.target.value)}
                  isDisabled={isLocked}
                >
                  <option value="ACTIVATED">ACTIVATED</option>
                  <option value="NEED-ACTIVATION">NEED-ACTIVATION</option>
                </Select>
                <FormErrorMessage>{errors.os_status?.[0]}</FormErrorMessage>
              </FormControl>
            </Grid>
          )}

          {/* MS Office */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">MS Office</FormLabel>
            <FormControl isInvalid={!!errors.ms_office?.length}>
              <Select
                placeholder="Select MS Office"
                value={asset.ms_office}
                onChange={(e) => set('ms_office', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(msOfficeOptions(), asset.ms_office).map((o) => (
                  <option key={String(o.id)} value={o.name}>
                    {o.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.ms_office?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Processor */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Processor</FormLabel>
            <FormControl isInvalid={!!errors.processor?.length}>
              <Select
                placeholder="Select Processor"
                value={asset.processor}
                onChange={(e) => set('processor', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(processorOptions, asset.processor).map((p) => (
                  <option key={String(p.id)} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.processor?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* CPU */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">CPU</FormLabel>
            <FormControl isInvalid={!!errors.cpu?.length}>
              <Select
                placeholder="Select CPU"
                value={asset.cpu}
                onChange={(e) => set('cpu', e.target.value)}
                isDisabled={isLocked}
              >
                {withCurrent(cpuOptions, asset.cpu).map((c) => (
                  <option key={String(c.id)} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.cpu?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* IP Address */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">IP Address</FormLabel>
            <FormControl isInvalid={!!errors.ip_address?.length}>
              <Input
                value={asset.ip_address}
                onChange={(e) => set('ip_address', e.target.value)}
                placeholder="e.g. 192.168.1.100"
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.ip_address?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* MAC Address */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">MAC Address</FormLabel>
            <FormControl isInvalid={!!errors.mac_address?.length}>
              <Input
                value={asset.mac_address}
                onChange={(e) => set('mac_address', e.target.value)}
                placeholder="e.g. 00:1A:2B:3C:4D:5E"
                isDisabled={isLocked}
              />
              <FormErrorMessage>{errors.mac_address?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          {/* Active */}
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
            <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
            <FormControl>
              <Checkbox
                isChecked={asset.active}
                onChange={(e) => set('active', e.target.checked)}
                colorScheme="blue"
                isDisabled={isLocked}
              >
                <Box as="span">Asset is active and available for assignment.</Box>
              </Checkbox>
            </FormControl>
          </Grid>
        </VStack>

        {/* ── Components section ── */}
        <Divider />
        <Heading size="sm" color="gray.600">
          Asset Components
        </Heading>

        {/* Components table */}
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Component Type</Th>
                <Th>Component Model</Th>
                <Th>Component Serial</Th>
                <Th textAlign="center" w="80px">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {components.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" color="gray.400" py={4}>
                    No components added yet
                  </Td>
                </Tr>
              ) : (
                components.map((c, i) => (
                  <Tr key={i}>
                    <Td>{c.component_type}</Td>
                    <Td>{c.component_model}</Td>
                    <Td>{c.component_serial}</Td>
                    <Td textAlign="center">
                      <IconButton
                        aria-label="Delete component"
                        icon={<Trash2 size={14} />}
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDeleteComponent(i)}
                        isDisabled={isLocked}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Add component row */}
        {!isLocked && (
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Add Component
            </Text>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr auto' }} gap={3} alignItems="end">
              <FormControl>
                <FormLabel fontSize="xs">Type</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. CPU, RAM, HDD"
                  value={newComponent.component_type}
                  onChange={(e) => setNewComponent((p) => ({ ...p, component_type: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Model</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. Intel i7, Samsung 16GB"
                  value={newComponent.component_model}
                  onChange={(e) => setNewComponent((p) => ({ ...p, component_model: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Serial</FormLabel>
                <Input
                  size="sm"
                  placeholder="e.g. SN123456789"
                  value={newComponent.component_serial}
                  onChange={(e) => setNewComponent((p) => ({ ...p, component_serial: e.target.value }))}
                />
              </FormControl>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<PlusCircle size={14} />}
                onClick={handleAddComponent}
                isDisabled={
                  !newComponent.component_type ||
                  !newComponent.component_model ||
                  !newComponent.component_serial
                }
              >
                Add
              </Button>
            </Grid>
          </VStack>
        )}

        {/* ── Form actions ── */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={saving}
            isDisabled={isLocked}
            loadingText={assetId ? 'Saving...' : 'Creating...'}
          >
            {assetId ? 'Save' : 'Create Asset'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
